<template>
  <div class="bug-icon-outer-circle" :class="loader()">
    <div class="bug-icon-inner-circle">
      <span class="material-icons" @click="onClick" role="button" v-if="showErrorReport">bug_report</span>
    </div>
  </div>
</template>

<script>
import html2canvas from 'html2canvas'
import axios from '../../axiosClient'
import file from '../../utils/file'
import notify from '@/utils/mixin-notify'
import { mapActions } from 'vuex'
import { get } from 'lodash'

/**
 * @class Screenshot
 *
 * @description taking and saving screenshots for helpdesk
 */
export default {
  name: 'Screenshot',
  mixins: [file, notify],
  props: {
    uploadUrl: {
      default: '/node/api/DocumentStorages/saveStringToFile',
    },
  },
  data() {
    return {
      createUrl: get(window, 'CORE.env.errorReportingUrl'),
      formField: get(window, 'CORE.env.errorReportingFormField'),
      canvas: null,
      dataUrl: null,
      imageUrl: null,
      formUrl: null,
      formData: null,
      attachmentId: null,
      loading: false,
      debug: false,
      isSafari: false,
    }
  },
  methods: {
    ...mapActions('popup', ['setOpened', 'setUrl']),
    showErrorReport() {
      return this.formField && this.createUrl
    },
    setFormUrl() {
      if (!this.createUrl) {
        console.error('missing client side environment variable: CORE.env.errorReportingUrl')
        return
      }
      if (!this.formField) {
        console.error('missing client side environment variable: CORE.env.errorReportingFormField')
        return
      }
      let url = `${window.location.protocol}//${window.location.host}${this.createUrl}`
      if (this.dataUrl) {
        url += `?${this.formField}[]=${this.attachmentId}`
      }
      url = encodeURI(url)
      return url
    },
    processData() {
      this.formData = new FormData()
      this.formData.append('data', this.dataUrl.split(',')[1])
      this.formData.append('mimeType', 'image/png')
      this.formData.append('fileName', this.getFileName())
      this.formData.append('foreignKey', '')
      this.formData.append('options', JSON.stringify({ encoding: 'base64' }))
    },
    getFileName() {
      return this.$CORE.url ? this.$CORE.url.replaceAll('/', '_') + '.png' : 'screenshot.png'
    },
    async postImage() {
      try {
        let formData = new FormData()
        formData.append('image', this.file)
        const response = await axios({ method: 'post'}).then(instance => instance.post(this.uploadUrl, this.formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }))

        this.attachmentId = response.data.id
        this.imageUrl = this.getDownloadUrl(this.attachmentId)
      } catch (error) {
        const noty = {
          text: this.$i18n.t('Screenshot post error'),
          type: 'error',
        }
        this.showNotification(noty)
        console.error(error)
        this.loading = false
      }
    },
    async getImage() {
      try {
        this.canvas = await html2canvas(document.body)
        this.dataUrl = this.canvas.toDataURL()
      } catch (error) {
        this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
        if (this.isSafari) {
          return
        }
        const noty = {
          text: this.$i18n.t('Screenshot generation error'),
          type: 'error',
        }
        this.showNotification(noty)
        console.error(error)
        this.loading = false
      }
    },
    openForm() {
      this.formUrl = this.setFormUrl()
      this.setUrl(this.formUrl)
      this.setOpened()
    },
    openImage() {
      const image = new Image()
      image.src = this.dataUrl

      const w = window.open('about:blank')
      w.document.write(image.outerHTML)
    },

    loader() {
      return this.loading ? 'loader' : ''
    },

    async onClick() {
      this.loading = true
      this.attachmentId = null
      await this.getImage()
      this.dataUrl && this.processData()
      if (this.debug) {
        this.dataUrl && this.openImage()
      } else {
        this.dataUrl && (await this.postImage())
        this.openForm()
      }
      this.loading = false
    },
  },
}
</script>

<style lang="scss">
.screenshot {
  position: relative;
  @keyframes screenshot-blink {
    0% {
      opacity: 0.2;
    }
    20% {
      opacity: 1;
    }
    100% {
      opacity: 0.2;
    }
  }
  .screenshot__button {
    background: none;
    outline: none;
    border: none;
  }
  .screenshot__sample-image {
    max-width: 600px;
    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }
  .screenshot__loader {
    position: absolute;
    bottom: -20px;
    font-size: 40px;
    text-align: center;
    width: 100%;
  }
  .screenshot__loader span {
    animation-name: screenshot-blink;
    animation-duration: 1.4s;
    animation-iteration-count: infinite;
    animation-fill-mode: both;
  }
  .screenshot__loader span:nth-child(2) {
    animation-delay: 0.2s;
  }
  .screenshot__loader span:nth-child(3) {
    animation-delay: 0.4s;
  }
  .material-icons {
    color: $icon-large;
  }
}

.bug-icon-outer-circle {
  padding: 3px;

  .bug-icon-inner-circle {
    padding: 1px;
  }

  &.loader {
    background-image: linear-gradient($icon-large, rgba(0,0,0,1));
    width: 100%;
    height: 100%;
    border-style: solid;
    border-color: transparent;
    border-radius: 50%;
    border-width: 0;
    animation: rotateCircle 2s linear infinite;
    display: table;

    .bug-icon-inner-circle {
      background: $sidebar-color;
      height: 100%;
      width: 100%;
      border-style: solid;
      border-color: transparent;
      border-radius: 50%;
      border-width: 1px;
      padding: 0;
      animation: rotateCircle 2s linear infinite reverse;
      display: table;
      text-align: center;
    }
  }

  @keyframes rotateCircle {
    0% {
      transform: rotate(0);
    }
    25% {
      transform: rotate(90deg);
    }
    50% {
      transform: rotate(180deg);
    }
    75% {
      transform: rotate(270deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}

</style>
